import * as React from "react"

import { cn } from "../../lib/utils"
import { dateSep, datePattern, weekNo } from "../../lib/dateFormat"

/**
 * Sanitise React.useId() (which returns e.g. ":r7:") into a valid HTML
 * name/id token. Colons work in `id` but are noisy in `name` and cause
 * Chrome's autofill heuristics to ignore the field.
 */
const sanitizeId = (raw) => (raw ? raw.replace(/[^a-zA-Z0-9_-]/g, "") : "")

// "2026-03-09" -> "09/03/2026", "2026-03-09T07:30" -> "09/03/2026 07:30".
// Browsers render <input type="date"> in the BROWSER's locale (mm/dd/yyyy on
// an en-US machine) and ignore both `lang` and CSS, so the only way to show
// day-first is to hide the native text and paint our own over it — see
// DateOverlay below.
export const dmy = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(String(v || ""))
  if (!m) return ""
  const sep = dateSep()
  return `${m[3]}${sep}${m[2]}${sep}${m[1]}${m[4] ? ` ${m[4]}:${m[5]}` : ""}`
}

// Hides the native date text. On a mouse+keyboard device the segments come
// back while the field has focus, so typing a date still works. Only used for
// datetime-local now — plain dates capture the digits themselves (below) so
// the day-first order is true for typing as well as for display.
export const dateTextHiddenCls =
  "text-transparent [@media(pointer:fine)]:focus:text-foreground"

export const DateOverlay = ({ value, disabled, className, placeholder, revealOnFocus = true, week }) => (
  <span
    aria-hidden
    className={cn(
      "pointer-events-none absolute inset-0 flex items-center gap-1 whitespace-nowrap pl-3 pr-7 text-base md:text-sm",
      revealOnFocus && "peer-focus:[@media(pointer:fine)]:opacity-0",
      value ? "" : "text-muted-foreground",
      disabled && "opacity-50",
      className
    )}
  >
    <span className="min-w-0 overflow-hidden">{dmy(value) || placeholder || datePattern()}</span>
    {week ? (
      <span
        data-testid="week-badge"
        className="shrink-0 overflow-hidden rounded-sm bg-muted px-1 text-[10px] font-semibold leading-4 text-muted-foreground"
      >
        W{week}
      </span>
    ) : null}
  </span>
)

// React tracks the value it last rendered, so assigning input.value directly
// is ignored. Go through the prototype setter and fire a bubbling `input`
// event — the standard way to drive a controlled input from outside React.
const commitValue = (el, value) => {
  if (!el) return
  const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set
  setter.call(el, value)
  el.dispatchEvent(new Event("input", { bubbles: true }))
}

const maskDMY = (digits) => {
  const slots = datePattern().split("")
  const positions = [0, 1, 3, 4, 6, 7, 8, 9]
  digits.split("").forEach((d, i) => { slots[positions[i]] = d })
  return slots.join("")
}

// A date typed day-first, the way the field reads. Keyboard entry has to be
// handled here because the hidden native segments stay in the browser's own
// (month-first) order — letting them through is how "09/03" silently became
// 3 September.
const useDayFirstTyping = (inputRef, { disabled, readOnly }) => {
  const [digits, setDigits] = React.useState("")

  const onKeyDown = (e) => {
    if (disabled || readOnly) return
    const el = inputRef.current
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault()
      const next = (digits + e.key).slice(0, 8)
      setDigits(next)
      if (next.length === 8) {
        const [dd, mm, yyyy] = [next.slice(0, 2), next.slice(2, 4), next.slice(4, 8)]
        const valid = +mm >= 1 && +mm <= 12 && +dd >= 1 && +dd <= 31 && +yyyy >= 1900
        if (valid) {
          commitValue(el, `${yyyy}-${mm}-${dd}`)
          setDigits("")
        } else {
          setDigits("")
        }
      }
      return
    }
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault()
      if (digits) setDigits(digits.slice(0, -1))
      else commitValue(el, "")
      return
    }
    // The native segments are invisible, so stepping through them with the
    // arrow keys would edit something the user cannot see.
    if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault()
  }

  return { digits, onKeyDown, reset: () => setDigits("") }
}

const Input = React.forwardRef(({ className, type, id, name, ...props }, ref) => {
  const autoId = sanitizeId(React.useId())
  const resolvedId = id || `input-${autoId}`
  // Prefer explicit name; fall back to explicit id; finally to the
  // auto-generated token. This silences Chrome's "form field element
  // should have an id or name" autofill warning while remaining fully
  // backwards-compatible with any component that already supplies one.
  const resolvedName = name || id || `input-${autoId}`
  // Only controlled date fields get the day-first overlay — an uncontrolled
  // one would show a stale value because we never see what the user picked.
  const isDate = type === "date" && props.value !== undefined
  const isDateTime = type === "datetime-local" && props.value !== undefined
  const innerRef = React.useRef(null)
  const setRefs = React.useCallback((node) => {
    innerRef.current = node
    if (typeof ref === "function") ref(node)
    else if (ref) ref.current = node
  }, [ref])
  const typing = useDayFirstTyping(innerRef, props)

  const el = (
    <input
      type={type}
      id={resolvedId}
      name={resolvedName}
      className={cn(
        "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        isDate && "peer text-transparent",
        isDateTime && `peer ${dateTextHiddenCls}`,
        className
      )}
      ref={setRefs}
      {...props}
      onKeyDown={isDate ? (e) => { typing.onKeyDown(e); props.onKeyDown?.(e) } : props.onKeyDown}
      onBlur={isDate ? (e) => { typing.reset(); props.onBlur?.(e) } : props.onBlur} />
  );
  if (!isDate && !isDateTime) return el
  const week = typing.digits ? null : weekNo(props.value)
  // Keep the painted date clear of any leading icon the field makes room for (pl-*).
  const padLeft = (className || "").match(/(?:^|\s)(pl-\S+)/)?.[1]
  return (
    <span className="relative block">
      {el}
      <DateOverlay
        value={typing.digits ? "" : props.value}
        disabled={props.disabled}
        revealOnFocus={isDateTime}
        placeholder={
          typing.digits ? maskDMY(typing.digits)
            : isDateTime ? `${datePattern()} --:--`
              : datePattern()
        }
        className={cn(padLeft, typing.digits && "text-foreground")}
        week={week}
      />
    </span>
  )
})
Input.displayName = "Input"

export { Input }
